import React from "react";
import styled from "styled-components";
import * as d3 from "d3";

const StyledTooltipContainer = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;
  z-index: 2000;
`;

const TooltipHeader = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
  font-size: 13px;
  font-weight: 100 !important;
`;

const TooltipContent = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TooltipColor = styled.div`
  background-color: ${(props) => props.bgColor};
  width: 12px;
  height: 12px;
  border: 1px solid #999;
`;

const TooltipKey = styled.div`
  margin: 0 4px;
  text-overflow: ellipsis;
  width: 190px;
  white-space: nowrap;
  overflow: hidden;
`;

const TooltipValue = styled.div`
  margin-right: 10px;
  text-align: right;
  width: 80px;
  white-space: nowrap;
`;

const TooltipLabel = styled.div`
  width: 190px;
  display: flex;
  align-items: center;
`;

const TooltipContentWrapper = styled.div`
  width: ${(props) => (props.large ? "560px" : "280px")};
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  height: ${(props) => props.height + "px"};
  @media only screen and (max-width: 1200px) {
    width: 280px;
  }
`;

const getCorrection = (mouseX, chartWidth, isSmallScreen) => {
  if (isSmallScreen) {
    const isLeftSide = mouseX < chartWidth * 0.4;
    return isLeftSide ? 260 : -100;
  }

  const isLeftSide = mouseX < chartWidth * 0.25;
  if (isLeftSide) {
    return chartWidth / 2 > 400 ? 400 : chartWidth / 2;
  }
  return -290;
};

const Entry = ({ label, value, color, unit }) => {
  return (
    <TooltipContent key={`${label}-value`}>
      <TooltipLabel>
        <TooltipColor bgColor={color}></TooltipColor>
        <TooltipKey>{label}</TooltipKey>
      </TooltipLabel>
      <TooltipValue>
        {value === "n/a" ? "n/a" : `${d3.format(".3f")(value)} ${unit}`}
      </TooltipValue>
    </TooltipContent>
  );
};

const Tooltip = ({
  dataPoint,
  mouseX,
  mouseY,
  title,
  unit,
  chartWidth,
  fieldsMap,
  filterFields,
  type,
}) => {
  const { time, value} = dataPoint || {};
  if (!time || !fieldsMap) {
    return null;
  }

  const currentFields = Object.entries(value).reduce(
    (acc, [fieldName, fieldValue]) => {
      if (
        fieldName !== "time" &&
        filterFields.has(fieldName) &&
        fieldsMap[fieldName]
      ) {
        const { color } = fieldsMap[fieldName] ?? {};
        acc.series.push({
          color,
          name: fieldName,
          value: fieldValue[`${type}link`],
        });
      }
      return acc;
    },
    { series: [], rest: undefined, total: undefined}
  );

  const isSmallScreen = chartWidth < 700;
  const correction = getCorrection(mouseX, chartWidth, isSmallScreen);

  const height =
    currentFields.series.length > 9
      ? Math.ceil((currentFields.series.length + 1) / 2) * 19
      : (currentFields.series.length + 1) * 19;

  return (
    <StyledTooltipContainer
      style={{
        top: mouseY - 105 + "px",
        left: mouseX + correction + "px",
      }}
      className="tooltip-live"
    >
      <TooltipHeader>{title}</TooltipHeader>
      <TooltipContentWrapper
        large={!isSmallScreen && currentFields.series.length > 9}
        height={height}
      >
        {currentFields.series.map(
          ({ name, color, value }, index) => {
            if (value === null || (isSmallScreen && index > 9)) {
              return null;
            }
            return (
              <Entry
                label={name}
                value={value}
                color={color}
                key={`tooltip-${name}`}
                unit={unit}
              />
            );
          }
        )}
      </TooltipContentWrapper>
    </StyledTooltipContainer>
  );
};

export default Tooltip;
